import { Page, SectionBlock } from "../components/Page";

/**
 * On-brand photography. Every image below ships in this repo under
 * /public/imagery so the brand book never breaks from external hosting.
 * Source originals live in projects/vihangam-yog/campaign/images.
 */
interface Photo {
  src: string;
  caption: string;
  category: "stillness" | "dawn" | "nature" | "people" | "sydney";
}

const ON_BRAND: Photo[] = [
  {
    src: "/imagery/in_project_awakening.jpg",
    caption: "Awakening. First light on a familiar face — quiet, unhurried.",
    category: "stillness",
  },
  {
    src: "/imagery/in_project_light_through_trees.jpg",
    caption:
      "Light through trees. Atmosphere over subject; weather over event.",
    category: "nature",
  },
  {
    src: "/imagery/in_project_meditation_nature.jpg",
    caption:
      "Sitting outdoors. Real moments — not posed serenity, not staged for a camera.",
    category: "people",
  },
  {
    src: "/imagery/in_project_still_water.jpg",
    caption: "Still water. Scale without grandeur. The horizon does the work.",
    category: "dawn",
  },
  {
    src: "/imagery/meetup_hero.webp",
    caption:
      "From a Sydney session. Local-specific photography always beats generic stock.",
    category: "sydney",
  },
];

const OFF_BRAND: { rule: string; why: string }[] = [
  {
    rule: "Performative meditation poses",
    why: "Studio shots of crossed-legs and outstretched arms read as wellness marketing, not practice. Skip anything that looks directed for the camera.",
  },
  {
    rule: "Cold blue desaturation",
    why: "Loses the warmth that holds our palette together. If a photo can't be tinted softly sepia and still feel right, it's not the picture.",
  },
  {
    rule: "Dramatic sunbeams + lens flare",
    why: "Theatrical, over-saturated, inspirational-poster energy. We're not that loud.",
  },
  {
    rule: "Stock-Buddha imagery, mala beads, incense smoke",
    why: "Reads as decorative spirituality. Our practice isn't aesthetic; the photography shouldn't pretend otherwise.",
  },
];

export function Imagery() {
  return (
    <Page
      number="06"
      title="Imagery"
      eyebrow="Photography direction"
      intro={
        <>
          We use real photography over illustration — mood over event, light
          over subject, weather over moment. Pictures should feel like they were
          taken quietly, not staged for a camera.
        </>
      }
    >
      <SectionBlock title="Direction">
        <div className="grid gap-3 md:grid-cols-2">
          {[
            [
              "Soft light, warm tone",
              "Golden hour, overcast morning, candlelit interior. Sepia tilt is fine. Cold blue light is not.",
            ],
            [
              "Mood over moment",
              "A path, a horizon, a hand. The picture is about how it feels, not what's happening.",
            ],
            [
              "Local where possible",
              "Sydney coast, parks, quiet streets. Specificity carries warmth.",
            ],
            [
              "Don't direct the body",
              "If a person is in frame, they shouldn't be posed in a meditation cliché. Sitting, walking, resting — yes. Performing serenity — no.",
            ],
            [
              "Photography over illustration",
              "We almost never use illustrations of people. Real bodies carry weight that drawn figures cannot.",
            ],
            [
              "Restraint with treatments",
              "Slight sepia, slight desaturation, slight grain. Never heavy filters, never gradients painted across the picture.",
            ],
          ].map(([t, b]) => (
            <div
              key={t}
              className="border-l-2 pl-5"
              style={{ borderColor: "var(--amber)" }}
            >
              <p className="font-display text-[20px] text-ink">{t}</p>
              <p className="mt-2 font-body text-[14px] leading-relaxed text-ink-soft">
                {b}
              </p>
            </div>
          ))}
        </div>
      </SectionBlock>

      <SectionBlock title="On-brand photography">
        <div className="grid gap-4 md:grid-cols-2">
          {ON_BRAND.map((p) => (
            <figure
              key={p.src}
              className="surface overflow-hidden"
              style={{ background: "var(--paper)" }}
            >
              <div className="aspect-[4/3] w-full overflow-hidden bg-bg-soft">
                <img
                  src={p.src}
                  alt={p.caption}
                  loading="lazy"
                  className="h-full w-full object-cover"
                  style={{ filter: "sepia(0.14) saturate(0.94)" }}
                />
              </div>
              <figcaption className="p-4">
                <p className="font-body text-[13px] text-ink-soft">
                  {p.caption}
                </p>
                <p className="mt-1 font-body text-[11px] tracking-[0.18em] uppercase text-ink-soft opacity-70">
                  From the Vihangam Yog photo library
                </p>
              </figcaption>
            </figure>
          ))}
        </div>
      </SectionBlock>

      <SectionBlock title="Off-brand — what to skip past">
        <div className="grid gap-4 md:grid-cols-2">
          {OFF_BRAND.map((d) => (
            <div
              key={d.rule}
              className="border-l-2 pl-5"
              style={{ borderColor: "var(--ink-soft)" }}
            >
              <p className="eyebrow text-ink-soft">Skip</p>
              <p className="mt-2 font-display text-[18px] text-ink">{d.rule}</p>
              <p className="mt-2 font-body text-[14px] leading-relaxed text-ink-soft">
                {d.why}
              </p>
            </div>
          ))}
        </div>
      </SectionBlock>

      <SectionBlock title="Treatment recipe">
        <p className="max-w-[640px] font-body text-[15px] leading-relaxed text-ink-soft">
          Apply restraint as a default. Every on-brand image above uses the same
          recipe.
        </p>
        <ul className="mt-5 grid gap-2 font-body text-[14px] text-ink md:grid-cols-3">
          <li className="border-l pl-4" style={{ borderColor: "var(--amber)" }}>
            <span className="font-display text-[16px] italic text-amber">
              Sepia
            </span>{" "}
            14–18%
          </li>
          <li className="border-l pl-4" style={{ borderColor: "var(--amber)" }}>
            <span className="font-display text-[16px] italic text-amber">
              Saturation
            </span>{" "}
            92–96%
          </li>
          <li className="border-l pl-4" style={{ borderColor: "var(--amber)" }}>
            <span className="font-display text-[16px] italic text-amber">
              Brightness
            </span>{" "}
            93–98%
          </li>
        </ul>
      </SectionBlock>

      <SectionBlock title="Sourcing & rights">
        <ul className="space-y-3 font-body text-[15px] text-ink">
          {[
            "Photos taken at our own Sydney sessions are preferred. Get explicit consent before publishing any identifiable face.",
            "For supplemental imagery, Unsplash is acceptable — license permits commercial use without attribution; we credit anyway when space allows.",
            "Pexels is a fine secondary source under similar terms.",
            "Avoid heavily processed editorial stock (Getty, iStock). The look reads commercial.",
            "Store curated, sized exports under projects/vihangam-yog/brandbook/public/imagery for the brand book; same for vy-web/public/images on the marketing site. Originals stay out of the repo.",
          ].map((l) => (
            <li
              key={l}
              className="flex gap-3 border-t pt-3 border-rule first:border-t-0 first:pt-0"
            >
              <span className="mt-2 inline-block h-1 w-3 shrink-0 bg-amber" />
              <span>{l}</span>
            </li>
          ))}
        </ul>
      </SectionBlock>
    </Page>
  );
}
